import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { ToastMessageOptions, MessageService } from 'primeng/api';
import { MessageModule } from 'primeng/message';
import { AuthenticationService } from 'src/app/shared/service/authentication.service';
import { LoginService } from 'src/app/shared/service/login.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonService } from 'src/app/shared/service/common.service';
import { VehicleType } from '../sidenav/sidenav.interface';
import { PrimecustomModule } from 'src/app/shared/primeng.custom.module';
import { CryptoService } from 'src/app/shared/service/crypto.service';
import { loginPageText } from '../../services/enums';
import { GoogleRecaptchaComponent } from '../../../shared/component/google-recaptcha/google-recaptcha.component';
import { UtilService } from '../../services/util.service';
import { ServerEventService } from 'src/app/core/services/server-event.service';
import { LOGIN } from 'src/app/shared/routeLink.constant';
import { RoleService } from 'src/app/shared/service/role.service';
import { Store } from '@ngrx/store';
import { loginSuccess, logout } from 'src/app/store/user/user.actions';
import {
  selectSelectedVehicle,
  selectVehicleTypes,
} from 'src/app/store/user/user.selectors';
import { LoadUserMenuList } from 'src/app/store/userMenuList/userMenuList.action';
import { environment } from 'src/environments/environment';
import { EvSavingsDialogComponent } from './ev-savings-dialog/ev-savings-dialog.component';
import { MicrosoftAuthService } from 'src/app/shared/service/microsoft-auth.service';
import { LoadingService } from '../../services/loading.service';
import { SpinnerComponent } from 'src/app/shared/component/spinner/spinner.component';
import { ConsentComponent } from './consent/consent.component';
import { take } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  setAllCountries,
  setPersistedAppContext,
} from 'src/app/store/appContext/app-context.actions';
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MessageModule,
    RouterModule,
    PrimecustomModule,
    FormsModule,
    GoogleRecaptchaComponent,
    EvSavingsDialogComponent,
    SpinnerComponent,
    ConsentComponent,
    SpinnerComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  animations: [
    trigger('fadeSlideIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px) scale(0.98)' }),
        animate(
          '220ms 60ms cubic-bezier(.2,.8,.2,1)',
          style({ opacity: 1, transform: 'none' })
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms ease-in',
          style({ opacity: 0, transform: 'translateY(6px) scale(0.98)' })
        ),
      ]),
    ]),
  ],
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  messages!: ToastMessageOptions[];
  vehicleType!: VehicleType[];
  selectedVehicleType!: any;
  loading = false;
  rememberMeUser = false;
  showPassword = false;
  svgPath: any = 'assets/img/common/';
  TextEnum = loginPageText;
  captcha_result: string | number = '';
  isEnableCaptcha = environment.enableCaptcha;
  siteKey = environment.recaptcha.siteKey;
  visible = false;
  showMainContent = true;
  showConsent = false;
  consentData: any;
  agreeCheckBox = false;
  isAgreeLoading: any = false;
  loginDetailsResponse: any;
  isConsentFlowActive = false;
  submitted = false;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthenticationService,
    private readonly loginService: LoginService,
    private readonly commonService: CommonService,
    private readonly route: ActivatedRoute,
    private readonly crypto: CryptoService,
    private readonly utilService: UtilService,
    private readonly sse: ServerEventService,
    private readonly roleService: RoleService,
    private readonly store: Store,
    private readonly microsoftAuth: MicrosoftAuthService,
    public loadingService: LoadingService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    // 1. Capture the URL parameters
    this.route?.queryParams?.subscribe((params) => {
      const urlParams = new URLSearchParams(window.location.search);

      // 2. Get the Code & Verifier
      const loginPayload = this.microsoftAuth.getLoginParamsFromUrl(urlParams);

      if (loginPayload) {
        this.signMicrosoft(loginPayload);
      } else {
        console.error('No code found in URL or missing verifier');
        this.utilService.redirectToLogin();
      }
    });

    this.loginForm = this.loginService.getLoginFormGroup();
    this.loginForm?.markAsUntouched();
    const rem = this.commonService.getlocalStorageItem('rememberMe');
    if (rem && rem == 'true') {
      this.rememberMeUser = JSON.parse(rem);
      const userData = this.commonService.getlocalStorageItem('user');
      if (userData) {
        const uservalues = JSON.parse(this.crypto.decrypt(userData));
        this.loginForm.patchValue({
          useremail: uservalues.useremail,
          userpassword: uservalues.userpassword,
          rememberMe: this.rememberMeUser,
        });
      }
    } else {
      localStorage.removeItem('user');
    }
    this.sse.closeEventSource();
  }

  signIn(): void {
    this.submitted = true;
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const loginFormValues = this.loginForm.value;

    const body = {
      username: loginFormValues.useremail,
      password: loginFormValues.userpassword,
      // captcha: loginFormValues.captcha
    };

    this.loginService.login(body).subscribe({
      next: (result: any) =>
        this.handleSignInSuccess(result?.data?.response, loginFormValues),

      error: (error: HttpErrorResponse) => this.handleSignInError(error),
    });
  }

  signMicrosoft(body: any): void {
    this.loadingService.show();
    this.loginService.loginWithMicrosoft(body).subscribe({
      next: (result: any) => {
        if (result.status) {
          this.handleSignInSuccess(result?.data?.response, null);
        } else {
          this.messages = [
            { severity: 'warn', summary: result?.data?.message },
          ];
          this.loadingService.hide();
        }
      },
      error: (error) => this.handleSignInError(error),
    });
  }

  private handleInvalidLoginForm(): void {
    this.loading = false;
    this.loginForm.markAllAsTouched();
  }

  private handleSignInSuccess(result: any, loginFormValues: any): void {
    const setTokenUser = {
      token: result.accessToken,
      refreshToken: result.refreshToken,
      originalRefreshToken: result.refreshToken,
      rememberMe: loginFormValues ? loginFormValues.rememberMe : false,
      user: loginFormValues
        ? this.crypto.encrypt(JSON.stringify(loginFormValues))
        : '',
    };

    this.setLocalStorageData(setTokenUser);
    this.processToken(result, loginFormValues);
  }

  private processToken(res: any, loginFormValues: any): void {
    this.loginService.loginSSO(res).subscribe({
      next: (ele) =>
        this.handleLoginSSOSuccess(ele, res.accessToken, loginFormValues),
      error: () => this.handleLoginSSOError(),
    });
  }

  private handleLoginSSOSuccess(
    ele: any,
    token: string,
    loginFormValues: any
  ): void {
    const setTokenUser = {
      token: token,
      rememberMe: loginFormValues ? loginFormValues.rememberMe : false,
      user: loginFormValues
        ? this.crypto.encrypt(JSON.stringify(loginFormValues))
        : '',
    };
    this.setLocalStorageData(setTokenUser);

    if (ele?.data?.response) {
      const data = ele.data.response;
      this.store.dispatch(
        setAllCountries({
          allCountries: data.availableCountries ?? [],
        })
      );
      this.store.dispatch(
        setPersistedAppContext({
          selectedCountry: data.availableCountries[0],
          originalUser: data.displayName ?? '',
          originalRole: data.roleName ?? '',
          isSuperAdmin: data.superAdmin ?? 'N',
        })
      );

      localStorage.setItem('originalToken', token ?? '');
      this.store.dispatch(
        loginSuccess({
          user: {
            name: data.displayName,
            email: data.email,
            phone: data.phoneNumber,
            roleName: data.roleName,
            roleCode: data.roleCode,
            uuid: data.uuid,
            vehicleTypes: data.vehicleTypeMap,
          },
        })
      );
      this.loginDetailsResponse = ele.data.response;
      this.openConsentForm(this.loginDetailsResponse);
    } else {
      this.handleInvalidResponse(ele);
    }

    this.roleService.initializeRoleFromStorage();
  }

  openConsentForm(resposne: any): void {
    if (!resposne.consent) {
      this.isConsentFlowActive = true;
      this.consentData = {
        consentTemplate: resposne.consentTemplate,
        consentVersion: resposne.consentVersion,
      };
      this.showConsent = true;
      this.commonService.setlocalStorageItem('CONSENT_PENDING', 'true');
    } else {
      this.processUserProfile(resposne);
    }
  }

  onAgree(): void {
    this.isAgreeLoading = true;
    this.loginService
      .consentAgree({
        consentVersion: this.consentData.consentVersion,
      })
      .subscribe({
        next: (res: any) => {
          if (res?.status) {
            this.isAgreeLoading = false;
            this.showConsent = false;
            this.isConsentFlowActive = false;
            this.commonService.removeConsent();
            this.processUserProfile(this.loginDetailsResponse);
          } else {
            this.isAgreeLoading = false;
            this.showWarn(res?.data?.message);
          }
        },
        error: () => {
          this.isAgreeLoading = false;
        },
      });
  }

  showWarn(message: string) {
    this.messageService.add({
      severity: 'warn',
      summary: 'Warning',
      detail: message,
    });
  }

  private processUserProfile(response: any): void {
    this.commonService.removeConsent();
    this.store
      .select(selectVehicleTypes)
      .pipe(take(1))
      .subscribe((vehicleTypes) => {
        this.vehicleType = vehicleTypes;
      });

    const defaultVehicleType = response.vehicleTypeMap?.[0] ?? null;
    this.store
      .select(selectSelectedVehicle)
      .pipe(take(1))
      .subscribe((vehicle) => {
        this.selectedVehicleType = vehicle || defaultVehicleType;
      });

    this.loadMenuLinks(response.vehicleTypeMap[0].code, response);
  }

  private handleInvalidResponse(ele: any): void {
    this.loadingService.hide();

    this.loading = false;
    this.messages = [{ severity: 'warn', summary: ele.data.message }];
    this.signOutAndRedirect();
  }

  private handleLoginSSOError(): void {
    this.loadingService.hide();

    this.loading = false;
    this.signOutAndRedirect();
  }

  private handleSignInError(error: HttpErrorResponse): void {
    this.loadingService.hide();
    this.loading = false;

    let message = '';

    if (error.error) {
      if (error.status === 503) {
        message = 'Service Temporarily Unavailable';
      } else {
        message = error.error?.data?.message;
      }
    }
    this.messages = [{ severity: 'warn', summary: message }];
  }

  private signOutAndRedirect(): void {
    if (this.isConsentFlowActive) {
      return;
    }
    this.utilService.logoutApi().subscribe((res: any) => {
      this.commonService.clearLocalStorageItems();
      sessionStorage.clear();
      this.router.navigate([`/${LOGIN}`]);
      this.store.dispatch(logout());
    });
  }

  redirectToPreviousUrl(): void {
    const returnUrl = this.route.snapshot.queryParams['returnUrl'] ?? '/';
    this.router.navigateByUrl(returnUrl);
  }

  onChangeVisiblePassword() {
    this.showPassword = !this.showPassword;
  }

  loadMenuLinks(vehicleCode: string, response: any) {
    this.utilService.loadMenuLinks(vehicleCode).subscribe({
      next: (navData: any) => {
        this.loadingService.hide();
        this.loading = false;
        this.store.dispatch(LoadUserMenuList({ menu: navData }));

        this.loginForm.reset();
        this.router.navigateByUrl('');

        setTimeout(() => {
          this.authService.setAuthSubject(response);
        }, 2);
      },
      error: (error) => {
        this.loadingService.hide();
        this.loading = false;
        this.signOutAndRedirect();
      },
    });
  }

  setLocalStorageData(objData: any) {
    Object.entries(objData).forEach(([key, value]) => {
      this.commonService.setlocalStorageItem(key, value as string);
    });
  }

  openPrivacy() {
    window.open(
      'https://www.montraelectric.com/privacy/',
      '_blank',
      'noopener'
    );
  }

  showDialog() {
    this.visible = true;
    this.showMainContent = false;
  }

  onDialogHide() {
    this.showMainContent = true;
  }
  async loginWithMicrosoft() {
    try {
      await this.microsoftAuth.login();
    } catch (err) {
      console.error('Microsoft login failed', err);
    }
  }
}
